<template>
    <node-view-wrapper>
        <div :class="writerClasses" @keydown.esc="removeAIInput">
            <div :class="inputContainerClasses">
                <input
                    v-model="inputValue"
                    type="text"
                    :disabled="quotaAILimitReached"
                    :class="inputClasses"
                    :placeholder="translate.labelPlaceholderInput"
                    @keydown.backspace="handleBackspace"
                    @keydown.enter="handleSubmit"
                />
                <button
                    :disabled="isDisabled"
                    class="sellsy-ai-writer__button"
                    @click="handleSubmit"
                >
                    <svg
                        width="13"
                        height="9"
                        viewBox="0 0 13 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M7.89852 0.192714C7.65477 0.448811 7.65477 0.865134 7.89852 1.12254L10.371 3.71898H0.623535C0.278535 3.71898 -0.00146484 4.01316 -0.00146484 4.37564C-0.00146484 4.73811 0.278535 5.0323 0.623535 5.0323H10.371L7.89852 7.63004C7.65477 7.88614 7.65477 8.30246 7.89852 8.55856C8.14227 8.81466 8.53852 8.81466 8.78227 8.55856L12.316 4.84449C12.4398 4.71579 12.4998 4.54505 12.4985 4.37564C12.4998 4.20622 12.4398 4.03549 12.316 3.90678L8.78227 0.192714C8.53852 -0.0633835 8.14227 -0.0633835 7.89852 0.192714Z"
                            fill="currentColor"
                        />
                    </svg>
                </button>
            </div>
            <div v-if="quotaAILimitReached" class="sellsy-ai-writer__container-limit">
                <div class="sellsy-ai-writer__container-limit__icon-label">
                    <img
                        src="/ressources/logged/img/icon/svg/ai-warning.svg"
                        alt="ai icon"
                        class="ai-icon"
                    />
                    <span class="typographies-paragraph-small-regular">
                        {{ translate.limit_quota }}
                    </span>
                </div>
                <div class="sellsy-ai-writer__container-limit__actions">
                    <CompassPlainButton
                        :label="translate.labelButtonContactUs"
                        size="small"
                        @click.prevent="handleContactUs"
                    />
                    <CompassPlainButton
                        :label="translate.labelButtonClose"
                        size="small"
                        @click.prevent="handleClose"
                    />
                </div>
            </div>
        </div>
    </node-view-wrapper>
</template>
<script>
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-2';
import SellsyAIRichTextMixin from 'mixins/SellsyAIRichTextMixin';
import Feature from 'Services/Feature';

export default {
    components: {
        NodeViewWrapper,
    },
    mixins: [SellsyAIRichTextMixin],
    props: nodeViewProps,
    data() {
        return {
            translate: {
                labelPlaceholderInput: this.$t.__('ai.SellsyAIInput.label_placeholder_input'),
                labelButtonContactUs: this.$t.__('ai.SellsyAIInput.label_button_contact_us'),
                labelButtonClose: this.$t.__('common.actions.close'),
                limit_quota: this.$t.__('ai.SellsyAIInput.limit_quota'),
            },
            inputValue: '',
        };
    },
    computed: {
        writerClasses() {
            return [
                'sellsy-ai-writer',
                this.quotaAILimitReached ? 'sellsy-ai-writer__is-limited' : '',
            ];
        },
        inputContainerClasses() {
            return [
                'sellsy-ai-writer__container-input',
                this.quotaAILimitReached ? 'sellsy-ai-writer__container-input__is-limited' : '',
            ];
        },
        inputClasses() {
            return ['sellsy-ai-writer__input', 'saas-custom-input'];
        },
        isDisabled() {
            return !this.inputValue || this.quotaAILimitReached;
        },
    },
    mounted() {
        document.addEventListener('pointerdown', this.handleClickOutside);
    },
    destroyed() {
        document.removeEventListener('pointerdown', this.handleClickOutside);
    },
    methods: {
        handleClickOutside(event) {
            event.stopImmediatePropagation();
            event.stopPropagation();

            if (!event.target.closest('.sellsy-ai-writer')) {
                this.removeAIInput();
            }
        },
        handleBackspace() {
            if (!this.inputValue) {
                this.removeAIInput();
            }
        },
        handleSubmit() {
            if (this.inputValue) {
                this.handleUserInput(this.inputValue);
                this.$store.dispatch(`${this.getHash}/setUseAI`, true);
            }
        },
        handleContactUs() {
            if (Feature.isEnabled('intercom')) {
                window.Intercom('show');
            }
        },
        handleClose() {
            this.removeAIInput();
        },
    },
};
</script>
<style lang="scss" scoped>
.sellsy-ai-writer {
    background: linear-gradient(#fff, #fff) padding-box, var(--colors-sellsy-ai-gradient) border-box;
    border-radius: var(--spacing-0);
    border: 1px solid transparent;
    box-shadow: var(--box-shadows-x-small);
    display: flex;
    flex-direction: column;
    gap: 2px;
    position: relative;

    &__is-limited {
        background: initial;
        border: 1px solid var(--colors-component-background-information-tag-disabled);
    }

    &__container {
        &-input {
            display: flex;
            gap: var(--spacing-1);
            padding: var(--spacing-1) 6px;

            &__is-limited {
                border-radius: var(--spacing-0) var(--spacing-0) 0 0;
                background: var(--colors-common-background-disabled);
                cursor: not-allowed;

                input:disabled {
                    cursor: not-allowed;
                }
            }
        }
        &-limit {
            justify-content: space-between;
            display: flex;
            padding: var(--spacing-1) var(--spacing-0);

            &__icon-label {
                display: flex;
                gap: var(--spacing-0);
                align-items: center;
                user-select: none;
                cursor: default;
            }

            &__actions {
                display: flex;
                gap: var(--spacing-3);
            }
        }
    }

    &__input {
        border: 0;
        flex-grow: 1;
        font-size: var(--font-size-14);
        font-weight: 400;
        line-height: 20px;
        padding: 0;
    }

    &__button {
        align-items: center;
        background: var(--colors-sellsy-ai-gradient);
        border: 0;
        border-radius: var(--spacing-0);
        color: var(--colors-common-text-standard-on-color);
        cursor: pointer;
        display: flex;
        flex-shrink: 0;
        height: 24px;
        justify-content: center;
        overflow: hidden;
        padding: 0;
        position: relative;
        transition: all 100ms cubic-bezier(0, 0, 0.3, 1);
        width: 24px;

        &:before {
            content: '';
            background: rgba(0, 0, 0, 0.2);
            bottom: 0;
            left: 0;
            opacity: 0;
            position: absolute;
            right: 0;
            top: 0;
            transition: all 100ms cubic-bezier(0, 0, 0.3, 1);
        }

        &:hover {
            &:before {
                opacity: 1;
            }
        }

        &[disabled='disabled'] {
            background: var(--colors-common-background-disabled);
            color: var(--colors-common-text-standard-disabled);
            cursor: not-allowed;

            &:before {
                content: none;
            }
        }

        svg {
            position: relative;
            z-index: 1;
        }
    }
}
</style>
